import { IconQuestionMarkOutline } from '@iwoca/icons';
import { GetMcaEstimatedScheduleResponse } from '@iwoca/lapi-client/edge';
import { Accordion, Pill } from '@iwoca/orion';
import cn from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';

import messages from './CustomiseOffer.messages';
import styles from './CustomiseOffer.module.css';
import { getOriginalPricing } from './getOriginalPricing';
import { useGetWithdrawalSummaryData } from './useGetWithdrawlSummaryData';
import { MoneyNoDecimals } from '../../../ui/moneyNoDecimals';
import { MoneyWithDecimals } from '../../../ui/moneyWithDecimals';
import { Rate } from '../../../ui/rate';

type WithdrawalSummaryProps = {
    accountId: string;
    schedule: GetMcaEstimatedScheduleResponse;
    loading: boolean;
    drawdownAmount: number;
};

export const WithdrawalSummary = ({
    schedule,
    loading,
    drawdownAmount,
    accountId
}: WithdrawalSummaryProps) => {
    const intl = useIntl();
    const { outstandingPrincipalWithFee, otherFees, isTopUpApproved } =
        useGetWithdrawalSummaryData(accountId);
    const summary = schedule?.data.summary;

    const originalFeeAmount = getOriginalPricing({
        numberOfRepayments: schedule?.data.estimated_schedule.repayments.length,
        totalCreditAmount: summary?.total_credit_amount
    });

    // set revised amount for displayed total principal after funding
    const revisedCapitalAmount = outstandingPrincipalWithFee + drawdownAmount;

    // only highlight upfront fee savings > 2.5%, i.e. where the new fee is 97.5% or below the current fee.
    const hasDiscountedPricingDE =
        summary.funding_fee <= 0.975 * originalFeeAmount || isTopUpApproved;

    return (
        <div className={styles.loanSummary}>
            <div className={styles.loanSummaryTitle}>
                <FormattedMessage {...messages.loanSummaryTitle} />
            </div>
            <div
                data-testid="withdrawal-amount-confirmation"
                className={styles.summaryRow}
            >
                <FormattedMessage {...messages.withdrawalAmountConfirmation} />
                <MoneyNoDecimals
                    className={cn(styles.summaryAmount, {
                        [styles.loading]: loading
                    })}
                    value={drawdownAmount}
                />
            </div>
            {hasDiscountedPricingDE && (
                <div className={styles.discountPill}>
                    <Pill>
                        <FormattedMessage
                            {...messages.discountedOffer}
                            values={{
                                amount: (
                                    <span>
                                        &thinsp;
                                        <MoneyWithDecimals
                                            className={cn({
                                                [styles.loading]: loading
                                            })}
                                            value={
                                                isTopUpApproved
                                                    ? otherFees
                                                    : originalFeeAmount -
                                                      summary.funding_fee
                                            }
                                        />
                                    </span>
                                )
                            }}
                        />
                    </Pill>
                </div>
            )}
            <div className={styles.summaryRow} data-testid="fee-summary">
                <div className={styles.feeColumn}>
                    <div>
                        <FormattedMessage {...messages.feeAmount} />
                    </div>
                    <div className={styles.feeNoInterestCopy}>
                        <FormattedMessage {...messages.noInterest} />
                    </div>
                </div>

                <div
                    data-testid="fee-amount"
                    className={styles.feeAmountColumn}
                >
                    {hasDiscountedPricingDE && (
                        <MoneyWithDecimals
                            className={cn(
                                styles.originalAmountWithStrikethrough,
                                {
                                    [styles.loading]: loading
                                }
                            )}
                            value={
                                isTopUpApproved
                                    ? summary.total_fees
                                    : originalFeeAmount
                            }
                            data-testid="original-amount"
                        />
                    )}
                    <MoneyWithDecimals
                        className={cn(styles.summaryAmount, {
                            [styles.loading]: loading
                        })}
                        value={summary.funding_fee}
                    />

                    <div
                        className={cn(styles.feePercentage, {
                            [styles.loading]: loading
                        })}
                    >
                        <Rate
                            className={styles.feePercentageAmount}
                            value={summary.funding_fee / revisedCapitalAmount}
                        />{' '}
                        <FormattedMessage {...messages.feeProportion} />
                    </div>
                </div>
            </div>
            {outstandingPrincipalWithFee && (
                <Accordion
                    className={styles.accordion}
                    title={intl.formatMessage(messages.howIsTheFeeCalculated)}
                    icon={<IconQuestionMarkOutline />}
                    data-testid="fee-accordion"
                >
                    <p className={styles.accordionExplanation}>
                        <FormattedMessage {...messages.feeExplanation} />
                    </p>
                    <div className={styles.accordionRow}>
                        <FormattedMessage {...messages.outstandingPrincipal} />
                        <MoneyWithDecimals
                            className={cn(styles.accordionAmount, {
                                [styles.loading]: loading
                            })}
                            value={outstandingPrincipalWithFee}
                        />
                    </div>
                    <div className={styles.accordionRow}>
                        <FormattedMessage {...messages.aditionalPayout} />
                        <MoneyWithDecimals
                            className={cn(styles.accordionAmount, {
                                [styles.loading]: loading
                            })}
                            value={drawdownAmount}
                        />
                    </div>
                    <div
                        className={cn(
                            styles.accordionRow,
                            styles.accordionSummary,
                            styles.gap
                        )}
                    >
                        <FormattedMessage {...messages.principalAfterFunding} />
                        <MoneyWithDecimals
                            className={cn({
                                [styles.loading]: loading
                            })}
                            value={revisedCapitalAmount}
                        />
                    </div>

                    <div className={styles.accordionRow}>
                        <FormattedMessage {...messages.fixedFeeForNewLoan} />
                        <MoneyWithDecimals
                            className={cn(styles.accordionAmount, {
                                [styles.loading]: loading
                            })}
                            value={summary.total_fees}
                        />
                    </div>

                    <div className={styles.accordionRow}>
                        <FormattedMessage {...messages.refund} />
                        <MoneyWithDecimals
                            className={cn(styles.accordionAmount, {
                                [styles.loading]: loading
                            })}
                            value={Math.min(otherFees, summary.total_fees) * -1}
                        />
                    </div>
                    <div
                        className={cn(
                            styles.accordionRow,
                            styles.accordionSummary
                        )}
                    >
                        <FormattedMessage {...messages.payableFee} />
                        <MoneyWithDecimals
                            className={cn({
                                [styles.loading]: loading
                            })}
                            value={summary.funding_fee}
                        />
                    </div>
                </Accordion>
            )}
            <div
                data-testid="total-repayment-amount"
                className={styles.summaryRow}
            >
                <FormattedMessage {...messages.totalRepayment} />

                <MoneyNoDecimals
                    className={cn(styles.summaryAmount, {
                        [styles.loading]: loading
                    })}
                    value={summary.total_payable}
                />
            </div>
        </div>
    );
};
